import React from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useDeleteMemberMutation } from "../../infrastructure/api/membersGroupsApi";
import type { Member } from "../../domain/entities/Member";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  member: Member | null;
}

export const RemoveMemberDialog: React.FC<Props> = ({
  open,
  onOpenChange,
  member,
}) => {
  const { t } = useTranslation("membersGroups");
  const [deleteMember, { isLoading }] = useDeleteMemberMutation();

  const handleConfirm = async () => {
    if (!member) return;
    try {
      await deleteMember(member.id).unwrap();
      toast.success(t("removeMemberDialog.success", { name: member.name }));
      onOpenChange(false);
    } catch {
      toast.error(t("removeMemberDialog.error"));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-xl sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">
            {t("removeMemberDialog.title")}
          </DialogTitle>
          <p className="text-sm text-gray-500 mt-1">
            {t("removeMemberDialog.confirm", { name: member?.name ?? "" })}
          </p>
        </DialogHeader>

        <div className="flex justify-end gap-3 pt-4">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
            className="px-4 py-5 text-primary border-primary hover:text-primary hover:bg-white cursor-pointer"
          >
            {t("removeMemberDialog.cancel")}
          </Button>

          <Button
            onClick={handleConfirm}
            loading={isLoading}
            className="bg-[#E53935] hover:bg-[#E53935] text-white px-4 py-5 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t("removeMemberDialog.remove")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
